/* eslint-disable react-hooks/exhaustive-deps */
import { withRouter } from "../_shared/withRouter";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import ConfirmationModal from "../template/_common/confirmModal";
import PaginationOwn from "../template/_common/ownPagination";
import LmsLearnerModal from "../template/admin/lmsLearnerModal";
import { globalAlert, globalLoader } from "../actions/commonActions";
import { exportData, logOut } from "../_shared/commonFunction";
import {
  lmsCreateLearnerApi,
  lmsDeleteLearnerApi,
  lmsGetLearnerApi,
  lmsListDistrictsApi,
  lmsListCoursesApi,
  lmsListRegionsApi,
  lmsListLearnerAccountsApi,
  lmsReviewRegistrationApi,
  lmsSendLearnerPasswordResetApi,
  lmsUpdateLearnerProfileApi,
  lmsUpdateLearnerStatusApi,
} from "./_lmsActions";

const STATUS_TABS = [
  { key: "all", label: "All", api: null },
  { key: "pending-approval", label: "Pending Approval", api: "pending-approval" },
  // { key: "invited", label: "Invited", api: "invited" },
  { key: "active", label: "Active", api: "active" },
  { key: "deactivated", label: "Deactivated", api: "deactivated" },
  { key: "rejected", label: "Rejected", api: "rejected" },
];

const EMPTY_STATES = {
  "pending-approval": [
    "No learners are waiting for approval.",
    "New registrations requiring manual approval will appear here.",
  ],
  invited: [
    "No learner invitations are pending.",
    "Learners with incomplete registration will appear here.",
  ],
  active: ["No active learners found.", "Approved learner accounts appear here."],
  deactivated: [
    "No learners are deactivated.",
    "Temporarily blocked learner accounts will appear here.",
  ],
  rejected: [
    "No registrations have been rejected.",
    "Rejected registration requests will appear here.",
  ],
  all: ["No learners found.", "Add a learner to get started."],
};

const learnerId = (learner) =>
  learner?.actionId || learner?.learnerId || learner?._id;
const accountActionId = (record) => record?.actionId || record?._id || record?.learnerId || record?.requestId;
const learnerName = (learner) =>
  learner?.name ||
  `${learner?.firstName || ""} ${learner?.lastName || ""}`.trim() ||
  "-";
const normalizeStatus = (status = "active") => {
  const value = String(status).toLowerCase().replace(/_/g, "-");
  if (["pending", "pending-approval", "awaiting-approval"].includes(value))
    return "pending-approval";
  if (["inactive", "deactivated", "suspended"].includes(value))
    return "deactivated";
  if (["invited", "invitation-sent", "pending-invite"].includes(value))
    return "invited";
  if (value === "rejected") return "rejected";
  return "active";
};
const AccountStatusBadge = ({ status }) => {
  const normalized = normalizeStatus(status);
  const config = {
    "pending-approval": { label: "Pending Approval", className: "badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" },
    invited: { label: "Not Active", className: "badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" },
    active: { label: "Active", className: "badge bg-success-subtle text-success border border-success-subtle" },
    deactivated: { label: "Deactivated", className: "badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle" },
    rejected: { label: "Rejected", className: "badge bg-danger-subtle text-danger-emphasis border border-danger-subtle" },
  }[normalized];
  return <span className={`lms-badge ${config.className}`}>{config.label}</span>;
};
const statusFromSearch = (search = "") => {
  const status = new URLSearchParams(search).get("status") || "all";
  return STATUS_TABS.some((item) => item.key === status) ? status : "all";
};
const countFrom = (counts, aliases) => {
  for (const alias of aliases) {
    if (counts?.[alias] != null) return Number(counts[alias]) || 0;
    const upper = alias.toUpperCase().replace(/-/g, "_");
    if (counts?.[upper] != null) return Number(counts[upper]) || 0;
  }
  return null;
};
const formatLastActive = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const learnerInitials = (learner) =>
  learnerName(learner)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (text.startsWith('="') && text.endsWith('"')) {
    return text;
  }
  return `"${text.replace(/"/g, '""')}"`;
};
const buildLearnerQueryParams = (page, limit, filters) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const status = STATUS_TABS.find((item) => item.key === filters.statusFilter);
  if (status?.api) params.set("status", status.api);
  if (filters.searchValue.trim()) params.set("search", filters.searchValue.trim());
  if (filters.courseFilter) params.set("courseId", filters.courseFilter);
  if (filters.enrolmentFilter)
    params.set("enrolmentStatus", filters.enrolmentFilter);
  if (filters.completionFilter)
    params.set("completionStatus", filters.completionFilter);
  return params;
};

const LmsLearners = (props) => {
  const { t } = useTranslation();
  const [navbarToggle, setNavbarToggle] = useState(false);
  const [allowView, setAllowView] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState(() =>
    statusFromSearch(props?.location?.search)
  );
  const [courseFilter, setCourseFilter] = useState("");
  const [enrolmentFilter, setEnrolmentFilter] = useState("");
  const [completionFilter, setCompletionFilter] = useState("");
  const [courseOptions, setCourseOptions] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [approveSuccess, setApproveSuccess] = useState(null); // { name, message }
  const [profileRecord, setProfileRecord] = useState(null);
  const [editLearner, setEditLearner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const visibleTabs = STATUS_TABS.filter((item) => !item.more);

  useEffect(() => {
    callBasicApi();
  }, [currentPage, pageSize, statusFilter]);

  useEffect(() => {
    const nextStatus = statusFromSearch(props?.location?.search);
    if (nextStatus !== statusFilter) {
      setStatusFilter(nextStatus);
      setCurrentPage(1);
    }
  }, [props?.location?.search]);

  useEffect(() => {
    const params = new URLSearchParams(props?.location?.search || "");
    if (!params.has("status")) {
      props?.history?.replace({
        pathname: "/admin/lms/learners",
        search: `?status=${statusFilter}`,
      });
    }
  }, []);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const res = await lmsListCoursesApi("?page=1&limit=200");
        if (res?.data?.status === 200) {
          const data = res?.data?.data || {};
          setCourseOptions(data?.list || data?.courses || []);
        }
      } catch (e) {
        /* Course filtering remains optional if this request is unavailable. */
      }
    };
    loadCourses();
  }, []);

  useEffect(() => {
    const loadRegionData = async () => {
      try {
        const [regionRes, districtRes] = await Promise.all([
          lmsListRegionsApi(),
          lmsListDistrictsApi(),
        ]);
        if (regionRes?.data?.status === 200) {
          const data = regionRes?.data?.data || {};
          setRegions(data?.list || data?.regions || []);
        }
        if (districtRes?.data?.status === 200) {
          const data = districtRes?.data?.data || {};
          setDistricts(data?.list || data?.districts || []);
        }
      } catch (e) {
        /* Profile editing still opens; district validation remains server-side. */
      }
    };
    loadRegionData();
  }, []);

  useEffect(() => {
    if (!openActionMenu) return undefined;
    const closeMenu = (event) => {
      if (!event.target.closest?.(".lms-learner-actions")) {
        setOpenActionMenu(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpenActionMenu(null);
    };
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openActionMenu]);

  const currentFilters = () => ({
    searchValue,
    statusFilter,
    courseFilter,
    enrolmentFilter,
    completionFilter,
  });

  const callBasicApi = async (page = currentPage, filters = currentFilters()) => {
    try {
      globalLoader(true);
      const params = buildLearnerQueryParams(page, pageSize, filters);

      console.log("call from basic API-->", params.toString());


      const res = await lmsListLearnerAccountsApi(`?${params.toString()}`);
      if (res?.data?.status === 200) {
        const data = res?.data?.data || {};
        console.log("from data learners----->", data);
        const list = data?.list || data?.learners || [];
        console.log("listed data --->>", list);
        const total =
          data?.pagination?.total ??
          data?.totalCount ??
          data?.total ??
          list.length;
        const counts =
          data?.statusCounts || data?.counts || data?.summary?.statusCounts || {};
        console.log("from counts data --->>", counts);
        setDataList(list);
        setTotalCount(total);
        setStatusCounts({
          all: countFrom(counts, ["all", "total"]) ?? 0,
          "pending-approval": countFrom(counts, ["pending-approval", "pending"]) ?? 0,
          invited: countFrom(counts, ["invited", "invitation-sent"]) ?? 0,
          active: countFrom(counts, ["active"]) ?? 0,
          deactivated: countFrom(counts, ["deactivated", "inactive", "suspended"]) ?? 0,
          rejected: countFrom(counts, ["rejected"]) ?? 0,
        });
        setAllowView(true);
      } else {
        globalAlert("error", res?.data?.message);
      }
      globalLoader(false);
    } catch (error) {
      globalAlert("error", error?.response?.data?.message);
      if (error?.response?.data?.status === 401)
        logOut(props?.history, "/admin/login");
      globalLoader(false);
    }
  };

  const exportFilteredLearners = async () => {
    try {
      globalLoader(true);
      const exportLimit = Math.max(Number(totalCount) || 0, Number(pageSize) || 25, 1000);
      const params = buildLearnerQueryParams(1, exportLimit, currentFilters());
      const res = await lmsListLearnerAccountsApi(`?${params.toString()}`);

      if (res?.data?.status === 200) {
        const data = res?.data?.data || {};
        const list = data?.list || data?.learners || [];
        const rows = [
          ["Learner", "Email", "Organisation", "Courses", "Last active", "Status"],
          ...list.map((learner) => {
            const enrolled = learner?.enrolledCount ?? learner?.enrolled ?? 0;
            return [
              learnerName(learner),
              learner?.email || "",
              learner?.learnerOrganisation || learner?.organisation || "",
              `${enrolled} ${Number(enrolled) === 1 ? "course" : "courses"}`,
              `="${formatLastActive(learner?.lastActiveAt || learner?.lastLoginAt || learner?.updatedAt)}"`,
              normalizeStatus(learner?.lifecycleStatus || learner?.status),
            ];
          }),
        ];
        const csv = rows.map((row) => row.map(csvCell).join(",")).join("\n");
        exportData(`\uFEFF${csv}`, `lms-learners-${Date.now()}.csv`, "text/csv;charset=utf-8;");
      } else {
        globalAlert("error", res?.data?.message);
      }
      globalLoader(false);
    } catch (error) {
      globalAlert("error", error?.response?.data?.message || "Unable to export learners.");
      if (error?.response?.data?.status === 401)
        logOut(props?.history, "/admin/login");
      globalLoader(false);
    }
  };

  const selectStatus = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    props?.history?.replace({
      pathname: "/admin/lms/learners",
      search: `?status=${status}`,
    });
  };

  const searchFilter = (event) => {
    event.preventDefault();
    if (currentPage !== 1) setCurrentPage(1);
    else callBasicApi(1);
  };

  const clearFilters = () => {
    const cleared = {
      searchValue: "",
      statusFilter,
      courseFilter: "",
      enrolmentFilter: "",
      completionFilter: "",
    };
    setSearchValue("");
    setCourseFilter("");
    setEnrolmentFilter("");
    setCompletionFilter("");
    if (currentPage !== 1) setCurrentPage(1);
    else callBasicApi(1, cleared);
  };

  const goToPageFunc = (event, direction) => {
    if (direction === "prev" && currentPage > 1)
      setCurrentPage(currentPage - 1);
    else if (
      direction === "next" &&
      currentPage < Math.ceil(totalCount / pageSize)
    )
      setCurrentPage(currentPage + 1);
    else if (event?.target?.value) setCurrentPage(Number(event.target.value));
  };

  const closeAddModal = async (value) => {
    if (!value) return setAddModalOpen(false);
    try {
      globalLoader(true);
      const res = await lmsCreateLearnerApi(value);
      if (res?.data?.status === 200) {
        globalAlert("success", res?.data?.message);
        setAddModalOpen(false);
        callBasicApi();
      } else {
        globalAlert("error", res?.data?.message);
        globalLoader(false);
      }
    } catch (error) {
      globalAlert("error", error?.response?.data?.message);
      globalLoader(false);
    }
  };

  const askAction = (type, learner, nextStatus = null) => {
    setAction({ type, learner, nextStatus });
    setConfirmOpen(true);
  };

  const confirmAction = async (confirmed) => {
    setConfirmOpen(false);
    if (!confirmed) {
      setAction(null);
      return;
    }
    if (action) {
      try {
        globalLoader(true);
        const res =
          action.type === "delete"
            ? await lmsDeleteLearnerApi(learnerId(action.learner))
            : ["pending-approval", "rejected"].includes(
              normalizeStatus(
                action.learner?.lifecycleStatus || action.learner?.status
              )
            )
              ? await lmsReviewRegistrationApi(accountActionId(action.learner), {
                action: action.type === "reject" ? "reject" : "approve",
                ...(action.type === "reject" ? { rejectReason: "Rejected by administrator" } : {}),
              })
              : await lmsUpdateLearnerStatusApi(
                learnerId(action.learner),
                action.nextStatus
              );
        if (res?.data?.status === 200) {
          // Approvals get a confirmation success dialog; other actions toast.
          if (action.type === "approve") {
            const approvedLearner = res?.data?.data?.learner;
            const approvedActionId = accountActionId(action.learner);
            if (approvedLearner) {
              setDataList((list) =>
                list.map((item) =>
                  accountActionId(item) === approvedActionId
                    ? {
                      ...action.learner,
                      ...approvedLearner,
                      recordType: "learner",
                      actionId: approvedLearner?._id || approvedLearner?.learnerId,
                      learnerId: approvedLearner?._id || approvedLearner?.learnerId,
                      hasLearnerAccount: true,
                      lifecycleStatus: normalizeStatus(approvedLearner?.status),
                    }
                    : item
                )
              );
            } else {
              setDataList((list) =>
                list.filter((item) => accountActionId(item) !== approvedActionId)
              );
            }
            // Approval also emails the learner a link to set their password.
            let resetSent = false;
            const id = approvedLearner?._id || approvedLearner?.learnerId || learnerId(action.learner);
            if (id) {
              try {
                const pr = await lmsSendLearnerPasswordResetApi(id);
                resetSent = pr?.data?.status === 200;
              } catch (e) {
                /* non-fatal — the learner is approved either way */
              }
            }
            globalLoader(false);
            setApproveSuccess({
              name: learnerName(action.learner),
              message: resetSent
                ? "The learner has been approved and a password reset link has been sent to their email so they can set their password."
                : "The learner has been approved. Use “Send reset password link” to email them a link to set their password.",
            });
          } else {
            globalLoader(false);
            globalAlert("success", res?.data?.message);
          }
          callBasicApi();
        } else {
          globalAlert("error", res?.data?.message);
          globalLoader(false);
        }
      } catch (error) {
        globalLoader(false);
        globalAlert("error", error?.response?.data?.message);
      }
    }
    setAction(null);
  };

  const openLearnerDetails = (learner) => {
    const id = learnerId(learner);
    if (learner?.recordType === "registration") {
      setProfileRecord(learner);
      return;
    }
    if (!id) return globalAlert("error", "Learner profile is unavailable.");
    props?.history?.push(`/admin/lms/learners/${id}`);
  };

  const regionName = (record) => {
    const regionId = record?.region?._id || record?.region;
    return (
      record?.region?.name ||
      regions.find((region) => String(region._id) === String(regionId))?.name ||
      record?.serviceArea ||
      "-"
    );
  };

  const districtName = (record) => {
    const districtId = record?.districtRef?._id || record?.districtRef || record?.district;
    return (
      record?.districtRef?.name ||
      districts.find((district) => String(district._id) === String(districtId))?.name ||
      record?.district ||
      "-"
    );
  };

  const openEditLearner = async (learner) => {
    if (learner?.recordType === "registration") return openLearnerDetails(learner);
    setEditLearner(learner);
    setEditForm({
      fullName: learnerName(learner),
      email: learner?.email || "",
      mobile: learner?.mobile || learner?.phone || learner?.phoneNumber || "",
      jobTitle: learner?.jobTitle || "",
      learnerOrganisation: learner?.learnerOrganisation || learner?.organisation || "",
      userType: learner?.userType || "user",
      region: learner?.region?._id || learner?.region || learner?.districtRef?.region?._id || learner?.districtRef?.region || "",
      district: learner?.districtRef?._id || learner?.districtId || learner?.district || "",
    });
    try {
      const res = await lmsGetLearnerApi(learnerId(learner));
      if (res?.data?.status === 200) {
        const detail = res?.data?.data?.learner || res?.data?.data || {};
        setEditLearner({ ...learner, ...detail });
        setEditForm({
          fullName: learnerName({ ...learner, ...detail }),
          email: detail?.email || learner?.email || "",
          mobile: detail?.mobile || detail?.phone || detail?.phoneNumber || "",
          jobTitle: detail?.jobTitle || "",
          learnerOrganisation: detail?.learnerOrganisation || detail?.organisation || "",
          userType: detail?.userType || "user",
          region: detail?.region?._id || detail?.region || detail?.districtRef?.region?._id || detail?.districtRef?.region || "",
          district: detail?.districtRef?._id || detail?.districtId || detail?.district || "",
        });
      }
    } catch (error) {
      globalAlert("error", error?.response?.data?.message || "Unable to load learner profile.");
    }
  };

  const setEdit = (key, value) => {
    setEditForm((form) => ({
      ...form,
      [key]: value,
      ...(key === "region" ? { district: "" } : {}),
    }));
  };

  const editDistrictOptions = districts.filter((district) => {
    if (!editForm?.region) return true;
    const regionId = district?.region?._id || district?.region;
    return String(regionId || "") === String(editForm.region);
  });
  const editDistrictHasCurrent = editDistrictOptions.some(
    (district) => String(district._id) === String(editForm?.district)
  );

  const saveEditLearner = async (event) => {
    event.preventDefault();
    if (!editForm.fullName?.trim()) return globalAlert("error", "Full Name is required");
    if (!editForm.jobTitle?.trim()) return globalAlert("error", "Job Title is required");
    if (!editForm.district) return globalAlert("error", "District is required");
    try {
      setEditSaving(true);
      const res = await lmsUpdateLearnerProfileApi(learnerId(editLearner), {
        fullName: editForm.fullName.trim(),
        mobile: editForm.mobile?.trim() || "",
        jobTitle: editForm.jobTitle.trim(),
        learnerOrganisation: editForm.learnerOrganisation?.trim() || "",
        userType: editForm.userType || "user",
        region: editForm.region || "",
        district: editForm.district,
      });
      setEditSaving(false);
      if (res?.data?.status === 200) {
        globalAlert("success", res?.data?.message || "Learner profile updated.");
        setEditLearner(null);
        callBasicApi();
      } else {
        globalAlert("error", res?.data?.message);
      }
    } catch (error) {
      setEditSaving(false);
      globalAlert("error", error?.response?.data?.message || "Unable to update learner profile.");
    }
  };

  const sendPasswordReset = async (learner) => {
    try {
      globalLoader(true);
      const res = await lmsSendLearnerPasswordResetApi(learnerId(learner));
      globalLoader(false);
      if (res?.data?.status === 200) globalAlert("success", res?.data?.message || "Password reset link sent.");
      else globalAlert("error", res?.data?.message);
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message || "Unable to send password reset link.");
    }
  };

  const renderActions = (learner) => {
    const status = normalizeStatus(learner?.lifecycleStatus || learner?.status);
    const hasLearnerAccount = !!(
      learner?.hasLearnerAccount ||
      learner?.learnerId ||
      learner?.learner
    );
    const id = learnerId(learner);
    const isLearner = learner?.recordType === "learner";
    const isRegistration = learner?.recordType === "registration";
    // Not-yet-approved: pending registrations, invited learners, or a rejected
    // registration an admin later re-approves.
    const needsApproval =
      isRegistration &&
      !hasLearnerAccount &&
      ["pending-approval", "rejected"].includes(status);

    const itemButton = (key, icon, label, onClick, className = "") => (
      <button key={key} type="button" className={`dropdown-item lms-action-menu-item lms-action-menu-item-${key} ${className}`} onClick={() => { setOpenActionMenu(null); onClick(); }}>
        <i className={`fas ${icon}`}></i><span>{label}</span>
      </button>
    );

    const menuItems = [];

    if (isLearner || isRegistration)
      menuItems.push(itemButton("profile", "fa-user", t("admin.lms.learners.profile", "Profile"), () => openLearnerDetails(learner)));
    if (needsApproval)
      menuItems.push(itemButton("approve", "fa-check", t("admin.lms.learners.approve", "Approve"), () => askAction("approve", learner, "active"), "text-success"));
    if (status === "pending-approval" && !hasLearnerAccount)
      menuItems.push(itemButton("reject", "fa-times", "Reject", () => askAction("reject", learner, "rejected"), "text-danger"));
    if (status === "active")
      menuItems.push(itemButton("deactivate", "fa-user-slash", t("common.deactivate", "Deactivate"), () => askAction("deactivate", learner, "inactive"), "text-warning"));
    if (status === "deactivated" && isLearner)
      menuItems.push(itemButton("reactivate", "fa-user-check", t("admin.lms.learners.reactivate", "Reactivate"), () => askAction("reactivate", learner, "active"), "text-success"));
    if (isLearner) {
      menuItems.push(itemButton("edit", "fa-user-edit", "Edit profile", () => openEditLearner(learner)));
      menuItems.push(itemButton("reset", "fa-key", "Send reset password link", () => sendPasswordReset(learner)));
    }
    if (status === "deactivated" && isLearner)
      menuItems.push(itemButton("delete", "fa-trash", "Delete learner", () => askAction("delete", learner), "text-danger"));

    return (
      <div className="lms-learner-row-actions">
        {menuItems.length > 0 && (
          <div className="lms-learner-actions dropdown">
            <button
              type="button"
              className="btn btn-sm lms-action-menu-btn"
              aria-expanded={openActionMenu === id}
              onClick={() => setOpenActionMenu((current) => (current === id ? null : id))}
              title={t("common.moreActions", "More actions")}
              aria-label={t("common.moreActions", "More actions")}
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>
            <div className={`dropdown-menu dropdown-menu-end lms-action-menu ${openActionMenu === id ? "show" : ""}`}>
              {menuItems}
            </div>
          </div>
        )}
      </div>
    );
  };

  const emptyState = EMPTY_STATES[statusFilter] || EMPTY_STATES.all;

  return (
    <>
      <Helmet>
        <title>Learners - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      {allowView && (
        <div className="admin-portal">
          <Header roleType="admin" />
          <div className={`${navbarToggle ? "active" : ""} wrapper`}>
            <SidebarComponent navbarToggle={navbarToggle} roleType="admin" menu="lms" subMenu="lms-learners" />
            <div className="main">
              <div className="main-wrapper">
                <p className="admin-banner">Learners</p>
                <SidebarArrow handleSidebarToggle={setNavbarToggle} />

                <div className="container-fluid pt-3 lms-learners-page">
                  <div className="lms-learners-heading">
                    <div>
                      <h1>Learners</h1>
                      <p>Manage learner accounts, approvals, invitations and course progress.</p>
                    </div>
                    <div className="lms-learners-primary-actions">
                      <button type="button" onClick={exportFilteredLearners} disabled={dataList.length === 0} className="btn secondary-btn btn-with-icon">
                        <i className="fas fa-file-csv"></i> Export CSV
                      </button>
                    </div>
                  </div>

                  <div className="lms-learner-status-tabs" aria-label="Learner account status">
                    {visibleTabs.map((item) => (
                      <button key={item.key} type="button" className={statusFilter === item.key ? "active" : ""} onClick={() => selectStatus(item.key)}>
                        <span>{item.label}</span><strong>{statusCounts[item.key] ?? 0}</strong>
                      </button>
                    ))}
                    {/* <div className="lms-status-more">
                      <button type="button" className={selectedMore ? "active" : ""} onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen}>
                        <span>{selectedMore ? activeStatus?.label : "More"}</span>
                        {selectedMore && <strong>{statusCounts[statusFilter] ?? 0}</strong>}
                        <i className={`fas fa-chevron-${moreOpen ? "up" : "down"}`}></i>
                      </button>
                      {moreOpen && (
                        <div className="lms-status-more-menu">
                          {moreTabs.map((item) => (
                            <button key={item.key} type="button" onClick={() => selectStatus(item.key)}>
                              <span>{item.label}</span><strong>{statusCounts[item.key] ?? 0}</strong>
                            </button>
                          ))}
                        </div>
                      )}
                    </div> */}
                  </div>

                  <form className="lms-learner-filters" onSubmit={searchFilter}>
                    <div className="lms-learner-search">
                      <i className="fas fa-search"></i>
                      <input className="form-control" placeholder="Search name or email..." value={searchValue} onChange={(event) => setSearchValue(event.target.value)} />
                    </div>
                    <div className="lms-filter-select">
                      <i className="fas fa-book-open lms-filter-select-icon" aria-hidden="true"></i>
                      <select aria-label="Filter by course" className="form-control" value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}>
                        <option value="">All courses</option>
                        {courseOptions.map((course) => <option key={course?._id || course?.courseId} value={course?._id || course?.courseId}>{course?.title}</option>)}
                      </select>
                      <i className="fas fa-chevron-down lms-filter-select-arrow" aria-hidden="true"></i>
                    </div>
                    <div className="lms-filter-select">
                      <i className="fas fa-user-check lms-filter-select-icon" aria-hidden="true"></i>
                      <select aria-label="Filter by enrolment status" className="form-control" value={enrolmentFilter} onChange={(event) => setEnrolmentFilter(event.target.value)}>
                        <option value="">All enrolments</option>
                        <option value="not_enrolled">Not enrolled</option>
                        <option value="enrolled">Enrolled</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                        <option value="expired">Expired</option>
                      </select>
                      <i className="fas fa-chevron-down lms-filter-select-arrow" aria-hidden="true"></i>
                    </div>
                    <div className="lms-filter-select">
                      <i className="fas fa-tasks lms-filter-select-icon" aria-hidden="true"></i>
                      <select aria-label="Filter by completion status" className="form-control" value={completionFilter} onChange={(event) => setCompletionFilter(event.target.value)}>
                        <option value="">All completion states</option>
                        <option value="not_started">Not started</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <i className="fas fa-chevron-down lms-filter-select-arrow" aria-hidden="true"></i>
                    </div>
                    <div className="lms-filter-actions">
                      <button type="submit" className="btn secondary-btn lms-filter-apply"><i className="fas fa-filter"></i> Apply</button>
                      <button type="button" className="btn lms-filter-reset" onClick={clearFilters}><i className="fas fa-undo-alt"></i> Reset</button>
                    </div>
                  </form>

                  <div className="lms-learner-list-card">
                    {dataList.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover lms-learner-table">
                          <thead>
                            <tr>
                              <th>Learner</th><th>Organisation</th><th>Courses</th><th>Last active</th><th>Status</th><th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dataList.map((learner, index) => {
                              const id = learnerId(learner);
                              const enrolled = learner?.enrolledCount ?? learner?.enrolled ?? 0;
                              return (
                                <tr key={id || index}>
                                  <td>
                                    <div className="lms-learner-identity">
                                      <span className="lms-learner-row-avatar">{learnerInitials(learner)}</span>
                                      <div className="lms-learner-identity-text">
                                        <strong>{learnerName(learner)}</strong>
                                        <span>{learner?.email || "-"}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>{learner?.learnerOrganisation || learner?.organisation || "-"}</td>
                                  <td>{enrolled} {Number(enrolled) === 1 ? "course" : "courses"}</td>
                                  <td>{formatLastActive(learner?.lastActiveAt || learner?.lastLoginAt || learner?.updatedAt)}</td>
                                  <td><AccountStatusBadge status={learner?.lifecycleStatus || learner?.status} /></td>
                                  <td>{renderActions(learner)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="lms-learner-empty"><i className="fas fa-user-clock"></i><strong>{emptyState[0]}</strong><p>{emptyState[1]}</p></div>
                    )}
                  </div>

                  {dataList.length > 0 && (
                    <div className="lms-learner-pagination">
                      <PaginationOwn pageSize={pageSize} changePageSize={(event) => { setPageSize(event.target.value); setCurrentPage(1); }} goToPage={goToPageFunc} totalCount={totalCount} currentPage={currentPage} applicationType="learners" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {addModalOpen && <LmsLearnerModal open={addModalOpen} closeModal={closeAddModal} />}
      {confirmOpen && (
        <ConfirmationModal
          open={confirmOpen}
          deleteInfo={[`${t("common.name")} : ${learnerName(action?.learner)}`]}
          description={`Are you sure you want to ${action?.type} this learner?`}
          onCloseModal={confirmAction}
        />
      )}
      {approveSuccess && (
        <div
          className="modal fade show lms-approve-success-modal"
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lms-approve-success-title"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content lms-approve-success-content">
              <span className="lms-approve-success-icon">
                <i className="fas fa-check-circle"></i>
              </span>
              <h3 id="lms-approve-success-title" className="lms-approve-success-title">
                Learner approved
              </h3>
              <p className="lms-approve-success-name">{approveSuccess.name}</p>
              <p className="lms-approve-success-text">{approveSuccess.message}</p>
              <button
                type="button"
                className="btn primary-btn lms-approve-success-btn"
                onClick={() => setApproveSuccess(null)}
                autoFocus
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {profileRecord && (
        <div className="modal fade show lms-learner-detail-modal" style={{ display: "block" }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered lms-learner-detail-dialog">
            <div className="modal-content">
              <div className="modal-header lms-learner-detail-header">
                <div className="lms-learner-detail-person">
                  <span className="lms-learner-avatar">{learnerInitials(profileRecord)}</span>
                  <div>
                    <h3>Profile</h3>
                    <span>{profileRecord.email || "-"}</span>
                  </div>
                </div>
                <button type="button" className="btn lms-modal-close" onClick={() => setProfileRecord(null)} aria-label="Close">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body lms-learner-detail-body">
                <section className="lms-learner-detail-section">
                  <div className="lms-learner-detail-section-title">
                    <div><i className="fas fa-user"></i><h4>Registration Information</h4></div>
                    <AccountStatusBadge status={profileRecord.lifecycleStatus || profileRecord.status} />
                  </div>
                  <div className="row">
                    {[
                      ["Full Name", learnerName(profileRecord)],
                      ["Email", profileRecord.email || "-"],
                      ["Organisation / School", profileRecord.learnerOrganisation || profileRecord.organisation || "-"],
                      ["Job Title", profileRecord.jobTitle || "-"],
                      ["Region", regionName(profileRecord)],
                      ["District", districtName(profileRecord)],
                      ["Service Audience", profileRecord.serviceAudience || "-"],
                      ["Service Area", profileRecord.serviceArea || "-"],
                      ["Newsletter Opt-in", profileRecord.newsletterOptIn ? "Yes" : "No"],
                      ["Requested On", formatLastActive(profileRecord.createdAt)],
                    ].map(([label, value]) => (
                      <div className="form-own-group col-md-6" key={label}>
                        <label className="form-own-label">{label}</label>
                        <div className="form-control lms-profile-readonly">{value}</div>
                      </div>
                    ))}
                  </div>
                  {profileRecord.rejectReason && (
                    <div className="form-own-group">
                      <label className="form-own-label">Reject Reason</label>
                      <div className="form-control lms-profile-readonly">{profileRecord.rejectReason}</div>
                    </div>
                  )}
                </section>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn primary-btn" onClick={() => setProfileRecord(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editLearner && (
        <div className="modal fade show lms-learner-detail-modal" style={{ display: "block" }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered lms-learner-detail-dialog">
            <div className="modal-content">
              <div className="modal-header lms-learner-detail-header">
                <div className="lms-learner-detail-person">
                  <span className="lms-learner-avatar">{learnerInitials(editLearner)}</span>
                  <div>
                    <h3 >Edit profile</h3>
                    <span>{editForm.email || "Email cannot be changed"}</span>
                  </div>
                </div>
                <button type="button" className="btn lms-modal-close" onClick={() => setEditLearner(null)} aria-label="Close">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form className="modal-body lms-learner-detail-body" onSubmit={saveEditLearner}>
                <section className="lms-learner-detail-section">
                  <div className="lms-learner-detail-section-title">
                    <div><i className="fas fa-user-edit"></i><h4>Organisation Admin</h4></div>
                  </div>
                  <div className="row">
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">Full Name *</label>
                      <input className="form-control" value={editForm.fullName || ""} onChange={(e) => setEdit("fullName", e.target.value)} />
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">Email</label>
                      <input className="form-control" value={editForm.email || ""} disabled readOnly />
                      <small className="text-muted">Email cannot be changed</small>
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">Mobile Number</label>
                      <input
                        className="form-control"
                        type="text"
                        maxLength="11"
                        value={editForm.mobile || ""}
                        onChange={(e) => {

                          const onlyNumbers = e.target.value.replace(/\D/g, "");
                          setEdit("mobile", onlyNumbers);
                        }}
                      />
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">Job Title *</label>
                      <input className="form-control" value={editForm.jobTitle || ""} onChange={(e) => setEdit("jobTitle", e.target.value)} />
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">Organisation / School</label>
                      <input className="form-control" value={editForm.learnerOrganisation || ""} onChange={(e) => setEdit("learnerOrganisation", e.target.value)} />
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">User Type</label>
                      <select className="form-control" value={editForm.userType || "user"} onChange={(e) => setEdit("userType", e.target.value)}>
                        <option value="user">User</option>
                        <option value="staff">Staff</option>
                      </select>
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">Region</label>
                      <select className="form-control" value={editForm.region || ""} onChange={(e) => setEdit("region", e.target.value)}>
                        <option value="">Select region</option>
                        {regions.map((region) => (
                          <option key={region._id} value={region._id}>{region.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-own-group col-md-6">
                      <label className="form-own-label">District *</label>
                      <select className="form-control" value={editForm.district || ""} onChange={(e) => setEdit("district", e.target.value)}>
                        <option value="">Select district</option>
                        {editForm.district && !editDistrictHasCurrent && (
                          <option value={editForm.district}>{editLearner?.district || editForm.district}</option>
                        )}
                        {editDistrictOptions.map((district) => (
                          <option key={district._id} value={district._id}>{district.name}</option>
                        ))}
                      </select>
                      {districts.length === 0 && <small className="text-muted">No districts configured yet.</small>}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-3" style={{ gap: 12 }}>
                    <button type="button" className="btn secondary-btn btn-own-grey" onClick={() => setEditLearner(null)}>Cancel</button>
                    <button type="submit" className="btn secondary-btn" disabled={editSaving}>
                      {editSaving ? <i className="fas fa-circle-notch fa-spin"></i> : "Save"}
                    </button>
                  </div>
                </section>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(LmsLearners);
